import axios from "axios";
import config from "@/config/config";
import {
  ImageUploadResponse,
  MediaService,
  MediaUploadResponse,
  UploadProgressCallback,
} from "./media.interfaces";
import { mockMediaService } from "./media.mocks";

class ApiMediaService implements MediaService {
  private baseUrl = `${config.apiBaseUrl}/admin/media`;

  async uploadVideo(
    file: File,
    onProgress?: UploadProgressCallback
  ): Promise<MediaUploadResponse> {
    const formData = new FormData();
    formData.append("video", file);

    try {
      const response = await axios.post<MediaUploadResponse>(
        `${this.baseUrl}/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(progress);
            }
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading video:", error);
      throw new Error("Failed to upload video");
    }
  }

  async uploadImage(
    file: File,
    onProgress?: UploadProgressCallback
  ): Promise<ImageUploadResponse> {
    const formData = new FormData();
    formData.append("image", file);

    try {
      const response = await axios.post<ImageUploadResponse>(
        `${this.baseUrl}/upload-image`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            if (onProgress && progressEvent.total) {
              const progress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onProgress(progress);
            }
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error("Error uploading image:", error);
      throw new Error("Failed to upload image");
    }
  }
}

const apiMediaService = new ApiMediaService();

// Export functions that conditionally use real or mock implementation
export const uploadVideo = async (
  file: File,
  onProgress?: UploadProgressCallback
): Promise<MediaUploadResponse> => {
  const service = config.useRealApi ? apiMediaService : mockMediaService;
  return service.uploadVideo(file, onProgress);
};

export const uploadImage = async (
  file: File,
  onProgress?: UploadProgressCallback
): Promise<ImageUploadResponse> => {
  const service = config.useRealApi ? apiMediaService : mockMediaService;
  return service.uploadImage(file, onProgress);
};
